<template>
  <div class="col-md-12 mb-6 odontogram">

    <div class="text-center py-10 text-muted" v-if="loading">
      <span class="spinner-border spinner-border-sm me-2"></span>
      Carregando tratamento...
    </div>

    <template v-else>
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3" v-if="treatment">
      <div class="fs-8 text-muted">
        Tratamento #{{ treatment.id_treatment }}
        <span class="mx-1">·</span>
        desde {{ openedAtLabel }}
        <span v-if="pendingProcedures"> · {{ pendingProcedures }} pendente(s)</span>
        <span v-if="viewingTreatmentId" class="badge badge-light-info ms-2">Consulta histórica</span>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end">
        <span class="badge" :class="statusBadgeClass">{{ statusLabel }}</span>
        <span class="text-muted fs-8" v-if="saving">Salvando...</span>
        <span class="text-success fs-8" v-else-if="saveStatus === 'saved'">Salvo</span>
        <button v-if="treatment.id_budget"
                type="button"
                class="btn btn-sm btn-light-success"
                @click="openBudget">
          Ver orçamento #{{ treatment.id_budget }}
        </button>
        <span v-if="showBudgetButton"
              class="d-inline-block"
              tabindex="0"
              :title="budgetButtonTooltip || undefined">
          <button type="button"
                  class="btn btn-sm btn-light-primary"
                  :disabled="!canGenerateBudget"
                  @click="openBudgetModal">
            Gerar orçamento
          </button>
        </span>
        <button v-if="schedule?.id_prof"
                type="button"
                class="btn btn-sm btn-light"
                @click="openHistoryModal">
          Tratamentos
        </button>
        <button v-if="viewingTreatmentId && openTreatment"
                type="button"
                class="btn btn-sm btn-light-primary"
                @click="returnToCurrentTreatment">
          Voltar ao atual
        </button>
        <button v-if="canClose"
                type="button"
                class="btn btn-sm btn-light-warning"
                @click="closeTreatment">
          Finalizar tratamento
        </button>
      </div>
    </div>

    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <label class="fs-6 fw-bold form-label mb-0">
        {{ attribute.label }} <span class="text-danger" v-if="attribute.required">*</span>
      </label>

      <div class="d-flex flex-wrap align-items-center gap-2" v-if="!isReadOnly">
        <span class="text-muted fs-8 me-1">Marcação rápida:</span>
        <button v-for="mode in quickModes"
                :key="mode.key"
                type="button"
                class="btn btn-sm"
                :class="activeMode === mode.key ? 'btn-primary' : 'btn-light'"
                :aria-pressed="activeMode === mode.key"
                @click="toggleMode(mode.key)">
          {{ mode.label }}
        </button>
        <button type="button"
                class="btn btn-sm btn-light-danger"
                :disabled="!canUndo"
                title="Desfazer (Ctrl+Z)"
                @click="undo">
          <i class="bi bi-arrow-counterclockwise"></i> Desfazer
        </button>
      </div>
    </div>

    <p class="text-muted fs-8 mb-2" v-if="activeMode && !isReadOnly">
      Clique nos dentes para marcar como <strong>{{ activeModeLabel }}</strong>.
      Clique de novo no mesmo dente ou face para remover.
      Clique em <em>{{ activeModeLabel }}</em> na barra para sair do modo rápido e abrir o detalhe do dente.
    </p>

    <div class="chart-wrap">
      <svg class="chart"
           :viewBox="layout.viewBox"
           role="group"
           :aria-label="'Odontograma, notação FDI, ' + dentitionLabel"
           @click="onClick"
           @keydown="onKeydown">

        <defs>
          <pattern :id="hatchId" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#ffffff" stroke-width="2.4" opacity="0.6"/>
          </pattern>
        </defs>

        <line :x1="layout.midline.x" :y1="layout.midline.y1"
              :x2="layout.midline.x" :y2="layout.midline.y2" class="midline"/>

        <g v-for="(tooth, index) in layout.teeth"
           :key="tooth.number"
           class="tooth"
           :class="{ 'is-selected': tooth.number === selectedTooth, 'is-disabled': isReadOnly }"
           :data-tooth="tooth.number"
           :tabindex="isReadOnly ? -1 : 0"
           :ref="'tooth-' + tooth.number"
           role="button"
           :aria-label="ariaFor(tooth)"
           @focus="focusedIndex = index">

          <polygon v-for="face in tooth.faces"
                   :key="face.code"
                   class="face"
                   :data-face="face.code"
                   :points="face.points"
                   :fill="fillFor(tooth.number, face.code)"
                   stroke="var(--odo-stroke, #8b7f87)"
                   stroke-width="1.1"/>

          <polygon v-for="face in hatchedFaces(tooth)"
                   :key="'hatch-' + face.code"
                   :points="face.points"
                   :fill="'url(#' + hatchId + ')'"
                   pointer-events="none"/>

          <g v-if="markFor(tooth.number) === 'cross'" pointer-events="none">
            <line :x1="tooth.x + 5" :y1="tooth.y + 5" :x2="tooth.x + tooth.size - 5" :y2="tooth.y + tooth.size - 5" class="mark"/>
            <line :x1="tooth.x + tooth.size - 5" :y1="tooth.y + 5" :x2="tooth.x + 5" :y2="tooth.y + tooth.size - 5" class="mark"/>
          </g>
          <circle v-else-if="markFor(tooth.number) === 'ring' || markFor(tooth.number) === 'implant'"
                  :cx="tooth.x + tooth.size / 2"
                  :cy="tooth.y + tooth.size / 2"
                  :r="tooth.size / 2 - 5"
                  fill="none"
                  class="mark"
                  pointer-events="none"/>
          <polygon v-else-if="markFor(tooth.number) === 'root'"
                   :points="rootMark(tooth)"
                   fill="none"
                   class="mark"
                   pointer-events="none"/>

          <text v-if="endoMark(tooth.number)"
                :x="tooth.labelX - 14"
                :y="tooth.labelY"
                class="endo-mark"
                :fill="endoMark(tooth.number).fill"
                pointer-events="none">E</text>

          <text :x="tooth.labelX" :y="tooth.labelY" class="tooth-num">{{ tooth.number }}</text>

          <circle v-if="procedureCount(tooth.number)"
                  :cx="tooth.labelX + 15"
                  :cy="tooth.labelY - 4"
                  r="4"
                  class="proc-dot"
                  pointer-events="none"/>
        </g>
      </svg>
    </div>

    <div class="legend">
      <span v-for="item in legend" :key="item.group + '-' + item.key" class="legend-item">
        <i class="swatch" :class="{ hatch: item.hatch }" :style="{ background: item.fill || 'var(--odo-tooth, #fff)' }"></i>
        <span v-if="item.mark === 'cross'" class="legend-mark" aria-hidden="true">×</span>
        <span v-else-if="item.mark === 'ring' || item.mark === 'implant'" class="legend-mark legend-ring" aria-hidden="true"></span>
        <span v-else-if="item.mark === 'root'" class="legend-mark" aria-hidden="true">△</span>
        <span v-else-if="item.mark === 'endo'" class="legend-mark endo" :style="{ color: item.fill }" aria-hidden="true">E</span>
        {{ item.label }}
      </span>
    </div>

    <div class="mt-4" v-if="state.procedures.length">
      <label class="fs-7 fw-bold form-label">Procedimentos</label>
      <div class="d-flex flex-column gap-2">
        <div v-for="procedure in state.procedures"
             :key="procedure.uid"
             class="item-card d-flex justify-content-between align-items-start gap-3">
          <div class="flex-grow-1 min-w-0">
            <div class="fw-semibold fs-7 text-gray-800">
              <span class="tooth-tag">{{ procedure.tooth }}</span>
              {{ procedure.name }}
            </div>
            <div class="text-muted fs-8 mt-1" v-if="procedureDetails(procedure)">
              {{ procedureDetails(procedure) }}
            </div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <select v-if="!isReadOnly && procedure.id_procedure"
                    class="form-select form-select-sm form-select-solid procedure-status-select"
                    :value="procedure.status"
                    @change="updateProcedureStatus(procedure, $event)">
              <option value="planned">Planejado</option>
              <option value="in_progress">Em andamento</option>
              <option value="done">Realizado</option>
            </select>
            <span v-else
                  class="badge"
                  :class="procedure.status === 'done' ? 'badge-light-success' : 'badge-light-warning'">
              {{ procedureStatusLabel(procedure.status) }}
            </span>
            <button v-if="!isReadOnly"
                    type="button"
                    class="btn btn-icon btn-sm btn-active-light-danger"
                    :aria-label="'Remover ' + procedure.name"
                    @click="removeProcedure(procedure.uid)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="separator separator-dashed my-6" aria-hidden="true"></div>

    <OdontogramToothModal v-if="!isReadOnly"
                          ref="modal"
                          :modal-id="modalId"
                          :tooth="selectedTooth"
                          :face="selectedFace"
                          :tooth-state="state.teeth[selectedTooth] || null"
                          :procedures="proceduresOf(selectedTooth)"
                          @save="applyFromModal"
                          @remove="removeProcedure"
                          @closed="restoreFocus"/>

    <OdontogramTreatmentHistoryModal v-if="schedule?.id_prof"
                                     ref="historyModal"
                                     :modal-id="historyModalId"
                                     :treatments-url="treatmentsUrl()"
                                     :active-id="treatment?.id_treatment ?? null"
                                     @select="viewTreatment"/>

    <OdontogramBudgetModal v-if="treatment?.id_treatment"
                           ref="budgetModal"
                           :modal-id="budgetModalId"
                           :treatment="treatment"
                           :treatments-url="treatmentsUrl()"
                           @generated="onBudgetGenerated"/>
    </template>
  </div>
</template>

<script>
// Swal e global, vindo do bundle do tema -- o pacote sweetalert2 nao e
// dependencia direta do projeto.
/* global Swal */
import http from '@/helpers/http';
import Alert from '@/helpers/alert';
import Download from '@/helpers/download';
import Loader from '@/helpers/loader';
import OdontogramToothModal from './OdontogramToothModal.vue';
import OdontogramTreatmentHistoryModal from './OdontogramTreatmentHistoryModal.vue';
import OdontogramBudgetModal from './OdontogramBudgetModal.vue';
import {
  ENDO_STATUS,
  FACE_LABELS,
  FACE_STATUS,
  QUICK_MODES,
  TOOTH_STATUS,
  applyQuickMark,
  buildLayout,
  buildLegend,
  centerFaceCode,
  hasActiveEndo,
  quadrantLabel,
} from './odontogram-layout';

const UNDO_LIMIT = 30;
const COMPACT_BREAKPOINT = 768;

export default {
  name: 'OdontogramInput',
  components: { OdontogramToothModal, OdontogramTreatmentHistoryModal, OdontogramBudgetModal },
  emits: ['onChange'],
  props: {
    attribute: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    schedule: { type: Object, default: null },
  },
  data() {
    return {
      treatment: null,
      openTreatment: null,
      viewingTreatmentId: null,
      loading: true,
      saving: false,
      saveStatus: null,
      saveTimer: null,
      state: this.emptyState(),
      selectedTooth: '',
      selectedFace: null,
      activeMode: null,
      history: [],
      focusedIndex: 0,
      isCompact: false,
      quickModes: QUICK_MODES,
    };
  },
  computed: {
    isReadOnly() {
      if (this.disabled || this.viewingTreatmentId) {
        return true;
      }

      return this.treatment && this.treatment.status !== 'open';
    },
    canClose() {
      return this.treatment?.status === 'open' && !this.disabled && !this.viewingTreatmentId;
    },
    showBudgetButton() {
      if (this.disabled || this.viewingTreatmentId || !this.treatment || this.treatment.id_budget) {
        return false;
      }

      return this.treatment.status === 'open';
    },
    canGenerateBudget() {
      return this.state.procedures.some((procedure) => procedure.status === 'planned');
    },
    budgetButtonTooltip() {
      if (!this.showBudgetButton || this.canGenerateBudget) {
        return '';
      }

      return 'Adicione ao menos um procedimento planejado no dente para gerar o orçamento.';
    },
    place() {
      return this.$store.state.auth.place;
    },
    openedAtLabel() {
      if (!this.treatment?.opened_at) {
        return '';
      }

      return new Date(this.treatment.opened_at).toLocaleDateString('pt-BR');
    },
    pendingProcedures() {
      return this.state.procedures.filter((procedure) => ['planned', 'in_progress'].includes(procedure.status)).length;
    },
    statusLabel() {
      return {
        open: 'Em andamento',
        closed: 'Encerrado',
        canceled: 'Cancelado',
      }[this.treatment?.status] ?? this.treatment?.status;
    },
    statusBadgeClass() {
      return this.treatment?.status === 'open' ? 'badge-light-primary' : 'badge-light-secondary';
    },
    layout() {
      return buildLayout(this.state.dentition);
    },
    dentitionLabel() {
      return this.state.dentition === 'deciduous' ? 'dentição decídua' : 'dentição permanente';
    },
    modalId() {
      return `odontogram_modal_${this.attribute.id_medrec_attr}`;
    },
    historyModalId() {
      return `odontogram_history_${this.attribute.id_medrec_attr}`;
    },
    budgetModalId() {
      return `odontogram_budget_${this.attribute.id_medrec_attr}`;
    },
    hatchId() {
      return `odontogram_hatch_${this.attribute.id_medrec_attr}`;
    },
    canUndo() {
      return this.history.length > 0;
    },
    activeModeLabel() {
      const mode = this.quickModes.find((m) => m.key === this.activeMode);
      return mode ? mode.label : '';
    },
    legend() {
      return buildLegend();
    },
  },
  watch: {
    'attribute.text'() {
      if (!this.treatment) {
        this.state = this.parseLegacyState();
      }
    },
  },
  mounted() {
    this.updateCompact();
    window.addEventListener('resize', this.updateCompact);
    window.addEventListener('keydown', this.onGlobalKeydown);
    this.loadTreatment();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateCompact);
    window.removeEventListener('keydown', this.onGlobalKeydown);
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
  },
  methods: {
    emptyState() {
      return {
        notation: 'fdi',
        dentition: this.attribute?.options?.dentition ?? 'permanent',
        teeth: {},
        procedures: [],
      };
    },

    treatmentsUrl() {
      return `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/treatments`;
    },

    parseTreatmentRef() {
      const text = this.attribute?.text;
      if (!text) {
        return null;
      }

      let parsed = text;
      if (typeof text === 'string') {
        try {
          parsed = JSON.parse(text);
        } catch (_) {
          return null;
        }
      }

      return parsed?.id_treatment ? parsed : null;
    },

    parseLegacyState() {
      const fallback = this.emptyState();
      const text = this.attribute?.text;
      if (!text) {
        return fallback;
      }

      let parsed = text;
      if (typeof text === 'string') {
        try {
          parsed = JSON.parse(text);
        } catch (_) {
          return fallback;
        }
      }

      if (parsed?.id_treatment) {
        return fallback;
      }

      return {
        ...fallback,
        ...parsed,
        teeth: parsed?.teeth ?? {},
        procedures: Array.isArray(parsed?.procedures) ? parsed.procedures : [],
      };
    },

    mapProcedure(procedure) {
      return {
        uid: String(procedure.id_procedure),
        id_procedure: procedure.id_procedure,
        tooth: procedure.tooth,
        faces: procedure.faces ?? [],
        id_item: procedure.id_item,
        procedure_value: procedure.procedure_value,
        name: procedure.procedure_name,
        status: procedure.status,
        obs: procedure.obs,
      };
    },

    applyTreatment(treatment) {
      this.treatment = treatment;
      const map = treatment.odontogram ?? this.emptyState();

      this.state = {
        notation: map.notation ?? 'fdi',
        dentition: map.dentition ?? this.emptyState().dentition,
        teeth: map.teeth ?? {},
        procedures: (treatment.procedures ?? []).map((procedure) => this.mapProcedure(procedure)),
      };
    },

    async loadTreatment() {
      if (!this.schedule?.id_prof || !this.schedule?.id_patient) {
        this.state = this.parseLegacyState();
        this.loading = false;
        return;
      }

      const base = this.treatmentsUrl();

      try {
        let treatment = null;
        const reference = this.parseTreatmentRef();

        if (reference?.id_treatment) {
          const response = await http.get(`${base}/${reference.id_treatment}`);
          treatment = response.data;
        } else if (!this.disabled) {
          try {
            const response = await http.get(`${base}/open`);
            treatment = response.data;
          } catch (error) {
            if (error.response?.status === 404) {
              const response = await http.post(base, { id_place: this.schedule.id_place });
              treatment = response.data;
            } else {
              throw error;
            }
          }
        }

        if (treatment) {
          this.applyTreatment(treatment);
          if (treatment.status === 'open') {
            this.openTreatment = treatment;
          }
          this.syncAttributeReference();
        } else {
          this.state = this.parseLegacyState();
        }
      } catch (_) {
        this.state = this.parseLegacyState();
        Alert.toastError('Erro ao carregar o tratamento odontológico.');
      } finally {
        this.loading = false;
      }
    },

    syncAttributeReference() {
      if (!this.treatment?.id_treatment) {
        return;
      }

      const text = JSON.stringify({ id_treatment: this.treatment.id_treatment });
      if (this.attribute.text !== text) {
        this.$emit('onChange', { ...this.attribute, text });
      }
    },

    queueSaveOdontogram() {
      if (this.isReadOnly || !this.treatment?.id_treatment) {
        return;
      }

      clearTimeout(this.saveTimer);
      this.saving = true;
      this.saveStatus = null;

      this.saveTimer = setTimeout(async () => {
        try {
          const response = await http.put(`${this.treatmentsUrl()}/${this.treatment.id_treatment}/odontogram`, {
            notation: this.state.notation,
            dentition: this.state.dentition,
            teeth: this.state.teeth,
          });

          this.applyTreatment(response.data);
          this.saveStatus = 'saved';
        } catch (_) {
          this.saveStatus = 'error';
          Alert.toastError('Erro ao salvar odontograma.');
        } finally {
          this.saving = false;
        }
      }, 500);
    },

    openHistoryModal() {
      this.$refs.historyModal?.open();
    },

    openBudgetModal() {
      this.$refs.budgetModal?.open();
    },

    onBudgetGenerated(treatment) {
      this.applyTreatment(treatment);
      if (treatment.status === 'open') {
        this.openTreatment = treatment;
      }
    },

    async viewTreatment(summary) {
      if (!summary?.id_treatment) {
        return;
      }

      if (summary.status === 'open' && this.openTreatment?.id_treatment === summary.id_treatment) {
        this.returnToCurrentTreatment();
        return;
      }

      if (summary.status === 'open') {
        this.openTreatment = summary;
        this.viewingTreatmentId = null;
        this.applyTreatment(summary);
        this.syncAttributeReference();
        return;
      }

      if (!this.openTreatment && this.treatment?.status === 'open') {
        this.openTreatment = this.treatment;
      }

      try {
        const response = await http.get(`${this.treatmentsUrl()}/${summary.id_treatment}`);
        this.viewingTreatmentId = summary.id_treatment;
        this.applyTreatment(response.data);
      } catch (_) {
        Alert.toastError('Erro ao carregar tratamento.');
      }
    },

    returnToCurrentTreatment() {
      if (!this.openTreatment) {
        return;
      }

      this.viewingTreatmentId = null;
      this.applyTreatment(this.openTreatment);
      this.syncAttributeReference();
    },

    async updateProcedureStatus(procedure, event) {
      const status = event.target.value;

      if (!this.treatment?.id_treatment || !procedure.id_procedure || procedure.status === status) {
        return;
      }

      const payload = { status };
      if (status === 'done') {
        payload.id_medrec_done = this.schedule?.id_medrec ?? null;
      }

      try {
        const response = await http.put(
          `${this.treatmentsUrl()}/${this.treatment.id_treatment}/procedures/${procedure.id_procedure}`,
          payload,
        );

        const index = this.state.procedures.findIndex((item) => item.uid === procedure.uid);
        if (index >= 0) {
          this.state.procedures.splice(index, 1, this.mapProcedure(response.data));
        }
      } catch (_) {
        event.target.value = procedure.status;
        Alert.toastError('Erro ao atualizar situação do procedimento.');
      }
    },

    async openBudget() {
      if (!this.treatment?.id_budget || !this.place?.id_place) {
        return;
      }

      Loader.show('Abrindo orçamento', 'Aguarde...');
      try {
        const response = await http.get(
          `/places/${this.place.id_place}/budgets/${this.treatment.id_budget}/pdf`,
          { responseType: 'blob' },
        );
        Download.openPdf(response.data);
      } catch (_) {
        Alert.toastError('Não foi possível abrir o orçamento.');
      } finally {
        Loader.close();
      }
    },

    async closeTreatment() {
      const result = await Swal.fire({
        title: 'Finalizar tratamento?',
        text: 'O ciclo atual ficará somente leitura. Um novo atendimento abrirá outro tratamento.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Finalizar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secundary',
        },
      });

      if (!result.isConfirmed || !this.treatment?.id_treatment) {
        return;
      }

      try {
        const response = await http.post(`${this.treatmentsUrl()}/${this.treatment.id_treatment}/close`);
        this.applyTreatment(response.data);
        this.openTreatment = null;
      } catch (_) {
        Alert.toastError('Erro ao finalizar tratamento.');
      }
    },

    updateCompact() {
      this.isCompact = window.innerWidth < COMPACT_BREAKPOINT;
    },

    // ---------- leitura de estado ----------

    fillFor(number, faceCode) {
      const tooth = this.state.teeth[number];
      if (!tooth) {
        return 'var(--odo-tooth, #ffffff)';
      }

      const toothStatus = TOOTH_STATUS[tooth.status];
      if (toothStatus && toothStatus.fill) {
        return toothStatus.fill;
      }

      const faceStatus = FACE_STATUS[(tooth.faces ?? {})[faceCode]];
      return (faceStatus && faceStatus.fill) || 'var(--odo-tooth, #ffffff)';
    },

    hatchedFaces(tooth) {
      const state = this.state.teeth[tooth.number];
      if (!state || (TOOTH_STATUS[state.status] && TOOTH_STATUS[state.status].fill)) {
        return [];
      }

      return tooth.faces.filter((face) => {
        const status = FACE_STATUS[(state.faces ?? {})[face.code]];
        return status && status.hatch;
      });
    },

    markFor(number) {
      const tooth = this.state.teeth[number];
      return tooth && TOOTH_STATUS[tooth.status] ? TOOTH_STATUS[tooth.status].mark : null;
    },

    endoMark(number) {
      const tooth = this.state.teeth[number];
      if (!hasActiveEndo(tooth)) {
        return null;
      }

      return ENDO_STATUS[tooth.endo.status];
    },

    rootMark(tooth) {
      const cx = tooth.x + tooth.size / 2;
      const top = tooth.y + 9;
      const bottom = tooth.y + tooth.size - 9;
      return `${cx},${bottom} ${cx - 8},${top} ${cx + 8},${top}`;
    },

    procedureCount(number) {
      return this.state.procedures.filter((p) => p.tooth === number).length;
    },

    proceduresOf(number) {
      return this.state.procedures.filter((p) => p.tooth === number);
    },

    procedureStatusLabel(status) {
      return { planned: 'Planejado', in_progress: 'Em andamento', done: 'Realizado' }[status] ?? status;
    },

    procedureDetails(procedure) {
      const parts = [];

      if (procedure.faces && procedure.faces.length) {
        parts.push(procedure.faces.map((code) => FACE_LABELS[code] ?? code).join(', '));
      }

      if (procedure.obs) {
        parts.push(procedure.obs);
      }

      return parts.join(' · ');
    },

    ariaFor(tooth) {
      const state = this.state.teeth[tooth.number];
      const status = state && TOOTH_STATUS[state.status] ? TOOTH_STATUS[state.status].label : 'Presente';
      const faces = Object.entries((state && state.faces) || {})
        .map(([code, value]) => `${code} ${FACE_STATUS[value] ? FACE_STATUS[value].label : value}`)
        .join(', ');
      const endo = hasActiveEndo(state) ? ENDO_STATUS[state.endo.status].label : '';

      const parts = [status];
      if (faces) {
        parts.push(faces);
      }
      if (endo) {
        parts.push(endo);
      }

      return `Dente ${tooth.number}, ${quadrantLabel(tooth.number)}, ${parts.join(', ')}`;
    },

    // ---------- interacao ----------

    onClick(event) {
      if (this.isReadOnly) {
        return;
      }

      const group = event.target.closest('[data-tooth]');
      if (!group) {
        return;
      }

      const tooth = group.dataset.tooth;
      // Em tela pequena a face tem ~14px, abaixo de qualquer alvo de toque
      // aceitavel: o clique seleciona o dente e a face escolhe-se no modal.
      const face = this.isCompact ? null : (event.target.dataset.face ?? null);

      if (this.activeMode) {
        this.applyQuick(tooth, face);
        return;
      }

      this.openModal(tooth, face);
    },

    onKeydown(event) {
      if (this.isReadOnly) {
        return;
      }

      const teeth = this.layout.teeth;
      const perRow = teeth.length / 2;
      let next = null;

      if (event.key === 'ArrowRight') next = this.focusedIndex + 1;
      if (event.key === 'ArrowLeft') next = this.focusedIndex - 1;
      if (event.key === 'ArrowDown') next = this.focusedIndex + perRow;
      if (event.key === 'ArrowUp') next = this.focusedIndex - perRow;

      if (next !== null) {
        event.preventDefault();
        const index = Math.max(0, Math.min(teeth.length - 1, next));
        this.focusTooth(teeth[index].number);
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const tooth = teeth[this.focusedIndex];
        if (!tooth) {
          return;
        }

        if (this.activeMode) {
          this.applyQuick(tooth.number, null);
        } else {
          this.openModal(tooth.number, null);
        }
      }
    },

    onGlobalKeydown(event) {
      if (this.isReadOnly) {
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && this.canUndo) {
        // So intercepta se o foco estiver dentro do odontograma, para nao
        // roubar o desfazer dos campos de texto do prontuario.
        if (this.$el.contains(document.activeElement)) {
          event.preventDefault();
          this.undo();
        }
      }
    },

    focusTooth(number) {
      const ref = this.$refs['tooth-' + number];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && el.focus) {
        el.focus();
      }
    },

    toggleMode(key) {
      this.activeMode = this.activeMode === key ? null : key;
    },

    openModal(tooth, face) {
      this.selectedTooth = tooth;
      this.selectedFace = face;

      this.$nextTick(() => {
        this.$refs.modal.load();
        window.bootstrap.Modal.getOrCreateInstance(document.getElementById(this.modalId)).show();
      });
    },

    restoreFocus() {
      if (this.selectedTooth) {
        this.focusTooth(this.selectedTooth);
      }
    },

    // ---------- mutacao ----------

    pushHistory() {
      this.history.push(JSON.stringify(this.state));
      if (this.history.length > UNDO_LIMIT) {
        this.history.shift();
      }
    },

    undo() {
      const previous = this.history.pop();
      if (!previous) {
        return;
      }

      this.state = JSON.parse(previous);
      this.queueSaveOdontogram();
    },

    toothEntry(number) {
      if (!this.state.teeth[number]) {
        this.state.teeth[number] = { status: 'present', faces: {} };
      }
      if (!this.state.teeth[number].faces) {
        this.state.teeth[number].faces = {};
      }
      return this.state.teeth[number];
    },

    applyQuick(number, face) {
      const entry = this.toothEntry(number);
      const changed = applyQuickMark(
        entry,
        face,
        this.activeMode,
        centerFaceCode(this.layout, number),
      );

      if (!changed) {
        return;
      }

      if (entry.status === 'absent') {
        delete entry.endo;
      }

      this.pushHistory();
      this.pruneToothIfEmpty(number);
      this.queueSaveOdontogram();
    },

    pruneToothIfEmpty(number) {
      const entry = this.state.teeth[number];
      if (!entry) {
        return;
      }

      if (entry.status === 'present' && Object.keys(entry.faces ?? {}).length === 0 && !hasActiveEndo(entry)) {
        delete this.state.teeth[number];
      }
    },

    async applyFromModal(payload) {
      this.pushHistory();
      const entry = this.toothEntry(this.selectedTooth);

      entry.status = payload.toothStatus;
      if (payload.toothStatus !== 'present') {
        entry.faces = {};
      } else if (payload.faceStatus) {
        if (payload.faceStatus.status === 'healthy') {
          delete entry.faces[payload.faceStatus.face];
        } else {
          entry.faces[payload.faceStatus.face] = payload.faceStatus.status;
        }
      }

      if (payload.toothStatus === 'absent' || !payload.endo) {
        delete entry.endo;
      } else {
        entry.endo = {
          status: payload.endo.status,
          obs: payload.endo.obs || '',
        };
      }

      this.pruneToothIfEmpty(this.selectedTooth);
      this.queueSaveOdontogram();

      if (payload.procedure && this.treatment?.id_treatment) {
        try {
          const response = await http.post(`${this.treatmentsUrl()}/${this.treatment.id_treatment}/procedures`, {
            tooth: this.selectedTooth,
            faces: payload.procedure.faces,
            procedure_name: payload.procedure.name,
            status: payload.procedure.status,
            obs: payload.procedure.obs || null,
          });

          this.state.procedures.push(this.mapProcedure(response.data));
        } catch (_) {
          Alert.toastError('Erro ao salvar procedimento.');
        }
      }

      window.bootstrap.Modal.getOrCreateInstance(document.getElementById(this.modalId)).hide();
    },

    removeProcedure(uid) {
      const procedure = this.state.procedures.find((p) => p.uid === uid);

      Swal.fire({
        title: 'Remover procedimento?',
        text: procedure ? `${procedure.name} — dente ${procedure.tooth}` : '',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secundary',
        },
      }).then(async (result) => {
        if (!result.isConfirmed) {
          return;
        }

        const procedure = this.state.procedures.find((item) => item.uid === uid);
        if (!procedure) {
          return;
        }

        this.pushHistory();

        if (this.treatment?.id_treatment && procedure.id_procedure) {
          try {
            await http.delete(`${this.treatmentsUrl()}/${this.treatment.id_treatment}/procedures/${procedure.id_procedure}`);
          } catch (_) {
            Alert.toastError('Erro ao remover procedimento.');
            return;
          }
        }

        this.state.procedures = this.state.procedures.filter((item) => item.uid !== uid);
      });
    },
  },
};
</script>

<style scoped>
.chart-wrap {
  width: 100%;
  overflow-x: auto;
}

.chart {
  display: block;
  width: 100%;
  max-width: 900px;
  height: auto;
  min-width: 520px;
  touch-action: none;
}

.tooth {
  cursor: pointer;
  outline: none;
}

.tooth.is-disabled {
  cursor: default;
}

.tooth:not(.is-disabled):hover .face {
  opacity: 0.72;
}

.tooth:focus-visible .face {
  stroke: #780f5a;
  stroke-width: 2;
}

.tooth.is-selected .tooth-num {
  fill: #780f5a;
  font-weight: 700;
}

.tooth-num {
  font-size: 12px;
  font-weight: 600;
  fill: #6f6169;
  text-anchor: middle;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.endo-mark {
  font-size: 15px;
  font-weight: 700;
  text-anchor: middle;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.midline {
  stroke: #e7dfe4;
  stroke-width: 1.5;
  stroke-dasharray: 5 6;
}

.mark {
  stroke: #5c6570;
  stroke-width: 2.2;
}

.proc-dot {
  fill: #780f5a;
}

.item-card {
  border: 1px solid var(--bs-gray-300, #e4e6ef);
  border-radius: 8px;
  padding: 12px 14px;
  background: var(--bs-body-bg, #fff);
  transition: border-color .15s ease, background-color .15s ease;
}

.item-card:hover {
  border-color: var(--bs-gray-400, #b5b5c3);
  background: var(--bs-gray-100, #f9f9f9);
}

/* O numero do dente e a informacao que o dentista procura primeiro na lista. */
.tooth-tag {
  display: inline-block;
  min-width: 30px;
  padding: 1px 7px;
  margin-right: 6px;
  border-radius: 5px;
  background: #f6ecf3;
  color: #780f5a;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.min-w-0 {
  min-width: 0;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 10px;
  font-size: 12px;
  color: #6f6169;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.swatch {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  border: 1px solid #8b7f87;
  flex: none;
}

.swatch.hatch {
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 1.5px,
    rgba(0, 0, 0, 0.28) 1.5px,
    rgba(0, 0, 0, 0.28) 3px
  );
}

.legend-mark {
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  color: #5c6570;
}

.legend-mark.endo {
  font-size: 15px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.legend-ring {
  width: 9px;
  height: 9px;
  border: 1.5px solid #5c6570;
  border-radius: 50%;
  display: inline-block;
}

.procedure-status-select {
  width: 9.5rem;
  min-width: 9.5rem;
}
</style>
